/**
 * Per-client single-domain detail page. Reached by clicking a domain name
 * on /clients/:clientId/domains (admin) or /portal/domains (client user).
 *
 * Admins get the full DomainDetailPanel (WHOIS refresh, nameservers,
 * renew/lock/autorenew, property block, live zone-records editor).
 * Client portal users get a read-only summary + DNS records list.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Globe2, Loader2, Server } from "lucide-react";
import { DomainDetailPanel } from "@/components/domains/DomainDetailPanel";
import { ZoneRecordsEditor, expiryBadge } from "@/components/domains/ZoneRecordsEditor";

export default function ClientDomainDetailPage() {
  const { clientId: routeClientId, domainId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const clientId = routeClientId || user?.client_id;

  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!clientId || !domainId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/domains`);
      const match = (data?.domains || []).find((d) => d.domain_id === domainId);
      if (!match) { setNotFound(true); setDomain(null); }
      else { setDomain(match); setNotFound(false); }
    } catch {
      setNotFound(true); setDomain(null);
    } finally { setLoading(false); }
  }, [clientId, domainId]);

  useEffect(() => { load(); }, [load]);

  const backHref = isAdmin ? `/clients/${clientId}/domains` : `/portal/domains`;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="client-domain-detail-loading">
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading domain…
        </CardContent></Card>
      </div>
    );
  }

  if (notFound || !domain) {
    return (
      <div className="space-y-4" data-testid="client-domain-detail-notfound">
        <Link to={backHref} className="inline-flex items-center gap-1.5 text-xs text-[#002FA7] hover:underline"
          data-testid="client-domain-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to domains
        </Link>
        <Card className="rounded-sm border-dashed"><CardContent className="p-10 text-center text-sm text-zinc-500">
          <Globe2 className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
          Domain not found or no longer assigned to this client.
        </CardContent></Card>
      </div>
    );
  }

  const badge = expiryBadge(domain.expiry_date);

  return (
    <div className="space-y-4" data-testid="client-domain-detail-page">
      <Link to={backHref} className="inline-flex items-center gap-1.5 text-xs text-[#002FA7] hover:underline"
        data-testid="client-domain-back-link">
        <ArrowLeft className="h-3 w-3" /> Back to domains
      </Link>

      <div className="flex items-center gap-2.5">
        <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#002FA7]/10">
          <Globe2 className="h-4 w-4 text-[#002FA7]" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display font-bold text-xl font-mono truncate">{domain.domain}</h2>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {domain.business_unit_id && (
              <Badge variant="outline" className={`rounded-sm text-[9px] ${
                domain.is_primary
                  ? "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30"
                  : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                {domain.is_primary ? "primary" : "redirect"}
              </Badge>
            )}
            <Badge variant="outline" className={`rounded-sm text-[9px] ${badge.cls}`}>
              {domain.expiry_date ? `expires ${domain.expiry_date} · ${badge.label}` : "expiry unknown"}
            </Badge>
            {domain.renewal_cost != null && (
              <span className="text-[10px] font-mono text-zinc-500">
                ${Number(domain.renewal_cost).toFixed(2)}
                {domain.renewal_cost_frequency === "monthly" ? "/mo"
                  : domain.renewal_cost_frequency === "quarterly" ? "/qtr"
                  : "/yr"}
              </span>
            )}
            <span className="text-[10px] font-mono text-zinc-400">TPP Wholesale</span>
          </div>
        </div>
      </div>

      {isAdmin ? (
        <Card className="rounded-sm overflow-hidden" data-testid="client-domain-admin-panel">
          <DomainDetailPanel domain={domain} onDomainUpdated={load} />
        </Card>
      ) : (
        <Card className="rounded-sm" data-testid="client-domain-portal-panel">
          <CardContent className="p-4 space-y-4">
            {(domain.nameservers || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono mb-1">Nameservers</div>
                <div className="flex items-start gap-1.5 text-[11px] text-zinc-700">
                  <Server className="h-3 w-3 mt-0.5 text-zinc-400" />
                  <span className="font-mono">{(domain.nameservers || []).join(" · ")}</span>
                </div>
              </div>
            )}
            <ZoneRecordsEditor domain={domain} readOnly onChanged={load} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
